import React from 'react'
import { BannerWrapper } from '@vp/banner-wrapper'
import type { DefaultContentfulBannerWrapperProps, ITrackingProps } from '@vp/banner-wrapper'
import { useTrackingContext } from '../../hooks/trackingContext'
import { createOnClickTrackingHandler, removeQueryParamsFrom } from '../../utils/trackingUtils'
import { trackMapEvent } from '../../utils/mapTracking'
import type { TrackingLinkConfig, MapTrackingConfig } from '../../types/homepageBanner'
import { useLogger } from '@vp/ubik-context'
import { BANNER_SCHEMA, BANNER_MAP_SCHEMA } from '../../utils/trackingSchemas'

interface IBannerProps {
  bannerProps: DefaultContentfulBannerWrapperProps[]
  locale: string
  imageLoadingStrategy?: string | undefined
  pageZone: string
  mapMetadata: {
    pageZone: string
    placementVariantId: string
    placementDefinitionId: string
  }
  usesMap?: boolean
}

interface IBannerExtraProps {
  imageOverrideProps: {
    loading: string | undefined
  }
}

export const Banner = (props: IBannerProps) => {
  const { bannerProps, imageLoadingStrategy, locale, pageZone, usesMap } = props
  const { sourcePagePath } = useTrackingContext()
  const logger = useLogger()

  React.useEffect(() => {
    if (usesMap && props.mapMetadata) {
      const { pageZone, placementVariantId, placementDefinitionId } = props.mapMetadata
      trackMapEvent(pageZone, placementVariantId, placementDefinitionId, 'view', logger, BANNER_MAP_SCHEMA)
    }
  }, [])

  function trackingMethod(event: React.MouseEvent<Element, MouseEvent>, { destinationUrl, linkIdentifier, ctaPosition, openInNewTab }: ITrackingProps) {
    const trackingConfig = buildTrackingPayload({
      destinationUrl: destinationUrl ?? '',
      linkIdentifier,
      ctaPosition,
      sourcePagePath,
      pageZone,
    })
    if (!trackingConfig) {
      return
    }

    // @ts-expect-error ubik property comes with all MCS entries
    const ubikEventTriggerId = props?.ubikTracking?.clickTriggerId

    const trackingAndNavigation = createOnClickTrackingHandler(
      trackingConfig,
      logger,
      BANNER_SCHEMA,
      destinationUrl,
      openInNewTab,
      undefined,
      usesMap ? (props.mapMetadata as MapTrackingConfig) : undefined,
      usesMap ? BANNER_MAP_SCHEMA : undefined,
      ubikEventTriggerId,
    )
    if (trackingAndNavigation) {
      trackingAndNavigation(event)
    }
  }

  const bannerExtraProps: IBannerExtraProps = {
    imageOverrideProps: {
      loading: imageLoadingStrategy,
    },
  }
  const bannerComponent = bannerProps && (
    <BannerWrapper bannerProps={bannerProps} extraProps={bannerExtraProps} trackingHandler={trackingMethod} locale={locale} />
  )

  return bannerComponent
}

const buildTrackingPayload = ({
  destinationUrl,
  linkIdentifier,
  ctaPosition,
  sourcePagePath,
  pageZone,
}: {
  destinationUrl: string
  linkIdentifier: string
  ctaPosition?: number | string
  sourcePagePath: string
  pageZone: string
}): TrackingLinkConfig => {
  const eventName = 'Navigation Clicked'
  const parsedDestinationPagePath = removeQueryParamsFrom(destinationUrl)
  const eventDetail = `${sourcePagePath};${parsedDestinationPagePath};${pageZone};${linkIdentifier}`

  return {
    eventName,
    properties: {
      category: 'Home Page',
      label: `Content:${pageZone}`,
      eventDetail,
      navigationDetail:
        ctaPosition && ((typeof ctaPosition === 'number' && ctaPosition > 0) || typeof ctaPosition === 'string')
          ? `${linkIdentifier}:${ctaPosition}`
          : linkIdentifier,
    },
    linkAttributes: {
      'data-section': `Content:${pageZone}`,
      'data-position': ctaPosition ?? 0,
      'data-translation': linkIdentifier,
    },
  }
}
